const LS_SETTINGS = 'blb_bible_app_settings';
const LS_BOOKMARKS = 'blb_bible_app_bookmarks';
const LS_HIGHLIGHTS = 'blb_bible_app_highlights';
const LS_LAST_READ = 'blb_bible_app_last_read';
const LS_NOTES = 'blb_bible_app_notes';
const LS_READING_PLAN = 'blb_bible_app_reading_plan';

function readJSON(key, fallback) {
	try {
		const raw = window.localStorage.getItem(key);
		return raw ? JSON.parse(raw) : fallback;
	} catch (e) {
		return fallback;
	}
}

function writeJSON(key, value) {
	try {
		window.localStorage.setItem(key, JSON.stringify(value));
	} catch (e) {
		/* localStorage unavailable (private mode/quota) — fail silently */
	}
}

export const settingsStore = {
	get(defaults) {
		return Object.assign({}, defaults, readJSON(LS_SETTINGS, {}));
	},
	set(settings) {
		writeJSON(LS_SETTINGS, settings);
	},
};

export const lastReadStore = {
	get() {
		return readJSON(LS_LAST_READ, null);
	},
	set(book, chapter) {
		writeJSON(LS_LAST_READ, { book, chapter, ts: Date.now() });
	},
};

export const bookmarksStore = {
	all() {
		return readJSON(LS_BOOKMARKS, []);
	},
	key(book, chapter, verse) {
		return `${book}-${chapter}-${verse}`;
	},
	has(book, chapter, verse) {
		const key = this.key(book, chapter, verse);
		return this.all().some((b) => this.key(b.book, b.chapter, b.verse) === key);
	},
	toggle(book, chapter, verse, text) {
		const list = this.all();
		const key = this.key(book, chapter, verse);
		const idx = list.findIndex((b) => this.key(b.book, b.chapter, b.verse) === key);
		if (idx > -1) {
			list.splice(idx, 1);
		} else {
			list.unshift({ book, chapter, verse, text, ts: Date.now() });
		}
		writeJSON(LS_BOOKMARKS, list);
		return idx === -1;
	},
	remove(book, chapter, verse) {
		const key = this.key(book, chapter, verse);
		const list = this.all().filter((b) => this.key(b.book, b.chapter, b.verse) !== key);
		writeJSON(LS_BOOKMARKS, list);
	},
};

export const highlightsStore = {
	all() {
		return readJSON(LS_HIGHLIGHTS, {});
	},
	key(book, chapter, verse) {
		return `${book}-${chapter}-${verse}`;
	},
	get(book, chapter, verse) {
		return this.all()[this.key(book, chapter, verse)] || null;
	},
	set(book, chapter, verse, color) {
		const all = this.all();
		const key = this.key(book, chapter, verse);
		if (color) {
			all[key] = color;
		} else {
			delete all[key];
		}
		writeJSON(LS_HIGHLIGHTS, all);
	},
};

export const notesStore = {
	all() {
		return readJSON(LS_NOTES, {});
	},
	key(book, chapter, verse) {
		return `${book}-${chapter}-${verse}`;
	},
	get(book, chapter, verse) {
		return this.all()[this.key(book, chapter, verse)] || null;
	},
	set(book, chapter, verse, text, verseText) {
		const all = this.all();
		const key = this.key(book, chapter, verse);
		const trimmed = (text || '').trim();
		if (trimmed) {
			all[key] = { book, chapter, verse, text: trimmed, verseText: verseText || '', ts: Date.now() };
		} else {
			delete all[key];
		}
		writeJSON(LS_NOTES, all);
	},
	remove(book, chapter, verse) {
		const all = this.all();
		delete all[this.key(book, chapter, verse)];
		writeJSON(LS_NOTES, all);
	},
};

export const readingPlanStore = {
	get() {
		return readJSON(LS_READING_PLAN, null);
	},
	start(planId, totalDays) {
		const state = {
			planId,
			totalDays,
			startedAt: Date.now(),
			completedDays: [],
		};
		writeJSON(LS_READING_PLAN, state);
		return state;
	},
	markDayComplete(day) {
		const state = this.get();
		if (!state) return null;
		if (!state.completedDays.includes(day)) {
			state.completedDays = [...state.completedDays, day].sort((a, b) => a - b);
			writeJSON(LS_READING_PLAN, state);
		}
		return state;
	},
	unmarkDayComplete(day) {
		const state = this.get();
		if (!state) return null;
		state.completedDays = state.completedDays.filter((d) => d !== day);
		writeJSON(LS_READING_PLAN, state);
		return state;
	},
	stop() {
		writeJSON(LS_READING_PLAN, null);
	},
};

const IDB_NAME = 'blb-bible-app';
const IDB_STORE = 'chapters';
const IDB_META = 'meta';
let dbPromise = null;

function openDb() {
	if (dbPromise) return dbPromise;

	if (!window.indexedDB) {
		dbPromise = Promise.resolve(null);
		return dbPromise;
	}

	dbPromise = new Promise((resolve) => {
		const req = window.indexedDB.open(IDB_NAME, 1);
		req.onupgradeneeded = () => {
			const db = req.result;
			if (!db.objectStoreNames.contains(IDB_STORE)) {
				db.createObjectStore(IDB_STORE);
			}
			if (!db.objectStoreNames.contains(IDB_META)) {
				db.createObjectStore(IDB_META);
			}
		};
		req.onsuccess = () => resolve(req.result);
		req.onerror = () => resolve(null);
	});

	return dbPromise;
}

export const chapterCache = {
	enabled: true,

	async ensureVersion(dataVersion) {
		const db = await openDb();
		if (!db) return;

		const stored = await this._metaGet(db, 'dataVersion');
		if (stored !== dataVersion) {
			await this._clearStore(db, IDB_STORE);
			await this._metaSet(db, 'dataVersion', dataVersion);
		}
	},

	async get(translation, book, chapter) {
		if (!this.enabled) return null;
		const db = await openDb();
		if (!db) return null;

		return new Promise((resolve) => {
			const tx = db.transaction(IDB_STORE, 'readonly');
			const req = tx.objectStore(IDB_STORE).get(`${translation}-${book}-${chapter}`);
			req.onsuccess = () => resolve(req.result || null);
			req.onerror = () => resolve(null);
		});
	},

	async set(translation, book, chapter, data) {
		if (!this.enabled) return;
		const db = await openDb();
		if (!db) return;

		return new Promise((resolve) => {
			const tx = db.transaction(IDB_STORE, 'readwrite');
			tx.objectStore(IDB_STORE).put(data, `${translation}-${book}-${chapter}`);
			tx.oncomplete = () => resolve();
			tx.onerror = () => resolve();
		});
	},

	async allCached() {
		const db = await openDb();
		if (!db) return [];

		return new Promise((resolve) => {
			const results = [];
			const tx = db.transaction(IDB_STORE, 'readonly');
			const req = tx.objectStore(IDB_STORE).openCursor();
			req.onsuccess = (e) => {
				const cursor = e.target.result;
				if (cursor) {
					results.push(cursor.value);
					cursor.continue();
				} else {
					resolve(results);
				}
			};
			req.onerror = () => resolve(results);
		});
	},

	_metaGet(db, key) {
		return new Promise((resolve) => {
			const tx = db.transaction(IDB_META, 'readonly');
			const req = tx.objectStore(IDB_META).get(key);
			req.onsuccess = () => resolve(req.result);
			req.onerror = () => resolve(undefined);
		});
	},

	_metaSet(db, key, value) {
		return new Promise((resolve) => {
			const tx = db.transaction(IDB_META, 'readwrite');
			tx.objectStore(IDB_META).put(value, key);
			tx.oncomplete = () => resolve();
			tx.onerror = () => resolve();
		});
	},

	_clearStore(db, name) {
		return new Promise((resolve) => {
			const tx = db.transaction(name, 'readwrite');
			tx.objectStore(name).clear();
			tx.oncomplete = () => resolve();
			tx.onerror = () => resolve();
		});
	},
};
